import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ElectricMopedOutlinedIcon from "@mui/icons-material/ElectricMopedOutlined";
import BatteryChargingFullOutlinedIcon from "@mui/icons-material/BatteryChargingFullOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function TermsAndConditions() {
  const lastUpdated = "August 15, 2026";

  const termsList = [
    {
      id: "agreement",
      icon: <GavelOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "1. Agreement & Company Overview",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            These Terms and Conditions ("Terms", "Agreement") constitute a legally binding agreement between you ("User", "Rider", "Customer") and <strong>VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong>, operating under the brand name <strong>RideXCity</strong> ("Company", "We", "Us").
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            By registering on our platform, unlocking, taking possession of, or operating any RideXCity electric scooter / EV fleet vehicle, utilizing battery swapping hubs, or accessing our digital services, you agree to comply with and be bound by these Terms.
          </Typography>
        </>
      ),
    },
    {
      id: "eligibility",
      icon: <AssignmentTurnedInOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "2. Eligibility & KYC Verification",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            To be eligible to rent and operate a RideXCity EV:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>You must be at least <strong>18 years of age</strong>.</li>
            <li>You must possess a valid, non-expired Government Photo ID (Aadhaar Card, Voter ID, or Passport).</li>
            <li>
              For high-speed / registered EV models requiring an RTO license under the Motor Vehicles Act, you must upload a valid and active <strong>Two-Wheeler Driving License</strong>.
            </li>
            <li>You must successfully complete online/offline KYC verification and digital agreement sign-off prior to vehicle release.</li>
            <li>You agree that the provided mobile number and WhatsApp account will serve as primary channels for all legal and service notices.</li>
          </Box>
        </>
      ),
    },
    {
      id: "rental-deposit",
      icon: <ReceiptLongOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "3. Rental Tenure, Security Deposit & Payments",
      content: (
        <>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Rental Plans:</strong> Vehicles are rented on daily, weekly, or monthly subscription plans as per the tariff displayed at the time of booking.
            </li>
            <li>
              <strong>Security Deposit:</strong> A refundable security deposit is collected before handover. The deposit is refunded within <strong>7 to 10 working days</strong> post-return, subject to comprehensive physical inspection, battery health check, and clearance of any pending challans/tolls.
            </li>
            <li>
              <strong>Subscription Renewals & Auto-Debit:</strong> Rental subscriptions must be renewed prior to the expiry of the active term. Failure to renew may result in automated remote vehicle immobilization and applicable late penalty charges.
            </li>
            <li>
              <strong>Non-Payment:</strong> Outstanding dues beyond 48 hours grant RideXCity the immediate right to repossess the EV without prior notice and initiate recovery proceedings.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "permitted-use",
      icon: <ElectricMopedOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "4. Permitted Vehicle Usage & Safety Rules",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            The rider agrees to operate the vehicle with utmost caution and adhere to the following mandatory guidelines:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Helmet Mandate:</strong> The rider (and pillion, if permitted) must wear an ISI-approved helmet at all times during operation.
            </li>
            <li>
              <strong>No Sub-leasing:</strong> The registered rider is strictly prohibited from transferring, lending, renting, or permitting any third party to operate the vehicle.
            </li>
            <li>
              <strong>Traffic Regulations:</strong> Adhere to speed limits, traffic signals, one-way signage, and all provisions of the Indian Motor Vehicles Act.
            </li>
            <li>
              <strong>Payload Limits:</strong> Do not exceed the maximum certified carrying weight of the vehicle (including rider and cargo/delivery bags).
            </li>
            <li>
              <strong>Substance Abuse:</strong> Riding under the influence of alcohol, drugs, or intoxicating substances is strictly prohibited and results in immediate vehicle repossession, deposit forfeiture, and reporting to police authorities.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "battery-telematics",
      icon: <BatteryChargingFullOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "5. Battery Swapping, Charging & Anti-Tampering",
      content: (
        <>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Battery Swapping:</strong> Riders can swap depleted batteries exclusively at authorized RideXCity Swap Stations using authorized QR / app tokens.
            </li>
            <li>
              <strong>Charging Safety:</strong> Use only RideXCity-approved charging cords and standard grounded power sockets. Do not attempt uncertified fast-charging setups.
            </li>
            <li>
              <strong>IoT & Telematics Tampering:</strong> Any attempt to disassemble the battery pack, tamper with BMS (Battery Management System), bypass IoT GPS trackers, cut wiring harnesses, or manipulate speed limiters is treated as intentional property damage and theft under the Indian Penal Code.
            </li>
            <li>
              <strong>Geofenced Boundaries:</strong> Vehicles are monitored via GPS. Crossing authorized city operating boundaries without written approval may trigger automatic speed throttling or remote motor shutdown.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "accidents-damage",
      icon: <WarningAmberOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "6. Accidents, Damages, Theft & Challans",
      content: (
        <>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Immediate Notification:</strong> In the event of an accident, collision, breakdown, or theft, the rider must inform RideXCity support immediately via <strong>+91 81231 98675</strong> and report to the nearest police station (FIR required in case of major accidents or theft).
            </li>
            <li>
              <strong>Damage Liability:</strong> The rider is responsible for any damage resulting from rash driving, negligence, water-logging submersion, off-road riding, or vandalism. Repair charges will be deducted from the security deposit or billed separately.
            </li>
            <li>
              <strong>Traffic Fines & E-Challans:</strong> Any traffic violation penalties, parking challans, or towing charges incurred during the user's rental period must be reimbursed in full by the rider.
            </li>
            <li>
              <strong>Third-Party Liability:</strong> RideXCity holds statutory insurance as required by law; however, rider indemnifies VALUEAID INFRASTRUCTURE PRIVATE LIMITED against third-party claims arising from gross rider negligence or rule violations.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "governing-law",
      icon: <HandshakeOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "7. Termination, Governing Law & Jurisdiction",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            <strong>Termination:</strong> RideXCity reserves the right to terminate any active rental agreement, immobilize the EV, and repossess the asset immediately upon breach of any terms stated herein.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            <strong>Governing Law:</strong> This Agreement shall be governed by, interpreted, and construed in accordance with the laws of the Republic of India.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            <strong>Jurisdiction:</strong> Any dispute, controversy, or claim arising out of or relating to this contract shall be subject to the exclusive jurisdiction of the competent courts located in <strong>Bengaluru, Karnataka, India</strong>.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ bgcolor: "#FAFBFC", py: { xs: 4, md: 8 }, minHeight: "80vh" }}>
      <Container maxWidth="lg">
        {/* Navigation Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <MuiLink
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 14,
              fontWeight: 600,
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to Home
          </MuiLink>
        </Stack>

        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3, fontSize: 13 }}>
          <MuiLink component={Link} to="/" underline="hover" color="inherit">
            Home
          </MuiLink>
          <Typography color="text.primary" sx={{ fontSize: 13, fontWeight: 600 }}>
            Terms & Conditions
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box
          sx={{
            bgcolor: "primary.dark",
            color: "primary.contrastText",
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            mb: 5,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.2)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -30,
              bottom: -30,
              width: 180,
              height: 180,
              bgcolor: "rgba(255,255,255,0.05)",
              borderRadius: "50%",
              pointerEvents: "none",
            }}
          />
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 2,
              fontWeight: 700,
              bgcolor: "rgba(255, 255, 255, 0.15)",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              display: "inline-block",
              mb: 2,
            }}
          >
            LEGAL AGREEMENT
          </Typography>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: 28, md: 40 }, mb: 1.5 }}>
            Terms & Conditions
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 700, fontSize: { xs: 14.5, md: 16 } }}>
            Please read these terms and conditions carefully before renting, operating, or accessing RideXCity electric vehicles and battery swapping services.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 2.5, opacity: 0.75, fontWeight: 500 }}>
            Last Updated: {lastUpdated} | VALUEAID INFRASTRUCTURE PRIVATE LIMITED
          </Typography>
        </Box>

        {/* Terms Content Sections */}
        <Stack spacing={4}>
          {termsList.map((sec) => (
            <Card
              key={sec.id}
              id={sec.id}
              sx={{
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  {sec.icon}
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, fontSize: { xs: 18, md: 21 }, color: "text.primary" }}>
                    {sec.title}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                {sec.content}
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
